import { Box, TextArea } from '@radix-ui/themes';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonTextHint } from 'components/common/form/text-hint';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { ITextareaInput } from 'interfaces/forms/text';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { debounce } from 'lodash';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import FloatingCopyButton from './floating-copy-button';

const PosRelativeContainer = ({ children }: PropsWithChildren) => (
  <Box position="relative">{children}</Box>
);

/**
 * Common textarea input component with optional debouncing.
 *
 * When debouncing is disabled, this works like a normal controlled text input (onChange + props.value).
 * When debouncing is enabled, we use a locally scoped state to handle what text is shown in the input, and only
 * calls the onChangeCallback after the specified delay.
 */
export const CommonTextareaInput = (props: ITextareaInput) => {
  // add an extra position: relative container if we need to render a floating copy button
  const Container = props.showCopyButton
    ? PosRelativeContainer
    : React.Fragment;

  const debounceEnabled = props.debounceMs && props.debounceMs > 0;

  // locally scoped state that's only really used when debouncing is enabled
  const [value, setValue] = useState(props.value);

  const onChangeCallback = useCallback(
    (v: string | undefined) => {
      if (props.onChange) {
        props.onChange(v);
      }
    },
    [props.onChange]
  );

  const latestOnChange = useLatest(onChangeCallback);

  const tryDebounce = useCallback(
    debounce(latestOnChange.current, props.debounceMs ?? 0),
    [props.debounceMs]
  );

  // if parent changes value => update local value
  useEffect(() => {
    setValue((prev) => {
      if (prev === props.value) {
        return prev;
      }
      return props.value;
    });
  }, [props.value]);

  // if user changes value using the input => debounce to parent
  useEffect(() => {
    if (!debounceEnabled) {
      return;
    }

    tryDebounce(value);
  }, [value, tryDebounce, debounceEnabled]);

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      <Container>
        <TextArea
          className={StringHelper.classNames([
            props.className,
            props.monospace ? 'font-mono' : undefined,
          ])}
          value={debounceEnabled ? value : props.value}
          disabled={props.disabled}
          color={props.inputColor}
          maxLength={props.maxLength}
          name={props.name}
          onChange={(e) => {
            const v = e.target.value;

            if (!debounceEnabled) {
              onChangeCallback(v);
              return;
            }

            setValue(v);
          }}
          placeholder={
            props.placeholder ? t(props.placeholder).toString() : undefined
          }
          required={!props.optional}
          rows={props.rows}
          resize="vertical"
        />

        {props.showCopyButton && props.value && (
          <FloatingCopyButton value={props.value} label={props.label} />
        )}
      </Container>

      <CommonTextHint {...props} />
    </CommonInputWrapper>
  );
};
